import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Package, Eye, Filter, Plus, Edit, Trash2, Upload, X } from 'lucide-react';
import { orderAPI, customerAPI } from '@/lib/api';

const OrderStatus = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newOrder, setNewOrder] = useState({
    customer_name: '',
    order_type: 'New Order',
    item_description: '',
    status: 'Pending',
    po_number: '',
    bag_no: '',
    order_date: '',
    delivery_date: '',
    notes: '',
    image_url: ''
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  // Load orders and customers on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [ordersData, customersData] = await Promise.all([
        orderAPI.getAll(),
        customerAPI.getAll()
      ]);
      setOrders(ordersData);
      setCustomers(customersData);
    } catch (error) {
      toast.error('Failed to load data');
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageSelect = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setImagePreview(base64String);
        
        if (isEdit && editingOrder) {
          setEditingOrder({ ...editingOrder, image_url: base64String });
        } else {
          setNewOrder({ ...newOrder, image_url: base64String });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = (isEdit = false) => {
    setImagePreview(null);
    setImageFile(null);
    
    if (isEdit && editingOrder) {
      setEditingOrder({ ...editingOrder, image_url: '' });
    } else {
      setNewOrder({ ...newOrder, image_url: '' });
    }
  };

  const handleAddOrder = async () => {
    if (!newOrder.customer_name || !newOrder.item_description) {
      toast.error('Please fill in customer name and item description');
      return;
    }

    try {
      const createdOrder = await orderAPI.create(newOrder);
      setOrders([...orders, createdOrder]);
      setNewOrder({
        customer_name: '',
        order_type: 'New Order',
        item_description: '',
        status: 'Pending',
        po_number: '',
        bag_no: '',
        order_date: '',
        delivery_date: '',
        notes: '',
        image_url: ''
      });
      setImagePreview(null);
      setIsAddDialogOpen(false);
      toast.success('Order added successfully');
    } catch (error) {
      toast.error('Failed to add order');
      console.error('Error adding order:', error);
    }
  };

  const handleEditOrder = async () => {
    if (!editingOrder.customer_name || !editingOrder.item_description) {
      toast.error('Please fill in customer name and item description');
      return;
    }

    try {
      const updated = await orderAPI.update(editingOrder.id, {
        customer_name: editingOrder.customer_name,
        order_type: editingOrder.order_type,
        item_description: editingOrder.item_description,
        status: editingOrder.status,
        po_number: editingOrder.po_number,
        bag_no: editingOrder.bag_no,
        image_url: editingOrder.image_url || '',
        order_date: editingOrder.order_date,
        delivery_date: editingOrder.delivery_date,
        notes: editingOrder.notes
      });
      setOrders(orders.map(o => o.id === editingOrder.id ? updated : o));
      setIsEditDialogOpen(false);
      setEditingOrder(null);
      toast.success('Order updated successfully');
    } catch (error) {
      toast.error('Failed to update order');
      console.error('Error updating order:', error);
    }
  };

  const handleDeleteOrder = async (id) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;

    try {
      await orderAPI.delete(id);
      setOrders(orders.filter(o => o.id !== id));
      toast.success('Order deleted successfully');
    } catch (error) {
      toast.error('Failed to delete order');
      console.error('Error deleting order:', error);
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'Completed':
      case 'Delivered':
        return 'default';
      case 'In Progress':
        return 'secondary';
      case 'Pending':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.item_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.po_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || order.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-lg bg-primary/10">
          <Package className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Order Status</h1>
          <p className="text-muted-foreground mt-1">Track and manage customer orders</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by customer, item, or PO number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-md"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Orders</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Delivered">Delivered</SelectItem>
                </SelectContent>
              </Select>
              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Order
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add New Order</DialogTitle>
                    <DialogDescription>Enter the order details below</DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="customer">Customer Name *</Label>
                      <Select
                        value={newOrder.customer_name}
                        onValueChange={(value) => setNewOrder({ ...newOrder, customer_name: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select customer" />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.map(customer => (
                            <SelectItem key={customer.id} value={customer.name}>
                              {customer.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="orderType">Order Type</Label>
                      <Select
                        value={newOrder.order_type}
                        onValueChange={(value) => setNewOrder({ ...newOrder, order_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="New Order">New Order</SelectItem>
                          <SelectItem value="Repair">Repair</SelectItem>
                          <SelectItem value="Custom">Custom</SelectItem>
                          <SelectItem value="Exchange">Exchange</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="item">Item Description *</Label>
                      <Textarea
                        id="item"
                        value={newOrder.item_description}
                        onChange={(e) => setNewOrder({ ...newOrder, item_description: e.target.value })}
                        placeholder="Enter item description"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="poNumber">PO Number</Label>
                        <Input
                          id="poNumber"
                          value={newOrder.po_number}
                          onChange={(e) => setNewOrder({ ...newOrder, po_number: e.target.value })}
                          placeholder="Optional"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="bagNo">Bag No</Label>
                        <Input
                          id="bagNo"
                          value={newOrder.bag_no}
                          onChange={(e) => setNewOrder({ ...newOrder, bag_no: e.target.value })}
                          placeholder="Optional"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="orderDate">Order Date</Label>
                        <Input
                          id="orderDate"
                          type="date"
                          value={newOrder.order_date}
                          onChange={(e) => setNewOrder({ ...newOrder, order_date: e.target.value })}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="deliveryDate">Expected Delivery</Label>
                        <Input
                          id="deliveryDate"
                          type="date"
                          value={newOrder.delivery_date}
                          onChange={(e) => setNewOrder({ ...newOrder, delivery_date: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={newOrder.status}
                        onValueChange={(value) => setNewOrder({ ...newOrder, status: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Pending">Pending</SelectItem>
                          <SelectItem value="In Progress">In Progress</SelectItem>
                          <SelectItem value="Completed">Completed</SelectItem>
                          <SelectItem value="Delivered">Delivered</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="orderImage">Order Image (Optional)</Label>
                      <div className="space-y-2">
                        <Input
                          id="orderImage"
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageSelect(e, false)}
                          className="cursor-pointer"
                        />
                        {(imagePreview || newOrder.image_url) && (
                          <div className="relative inline-block">
                            <img 
                              src={imagePreview || newOrder.image_url} 
                              alt="Order preview" 
                              className="h-32 w-32 object-cover rounded-md border"
                            />
                            <Button
                              type="button"
                              size="icon"
                              variant="destructive"
                              className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                              onClick={() => handleRemoveImage(false)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Upload an image of the order (Max 5MB)
                        </p>
                      </div>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        value={newOrder.notes}
                        onChange={(e) => setNewOrder({ ...newOrder, notes: e.target.value })}
                        placeholder="Additional notes (optional)"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleAddOrder}>Add Order</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">No orders found</p>
              <p className="text-muted-foreground">Add your first order to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>PO Number</TableHead>
                    <TableHead>Order Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell className="font-medium">{order.customer_name}</TableCell>
                      <TableCell>{order.item_description}</TableCell>
                      <TableCell>{order.order_type}</TableCell>
                      <TableCell>{order.po_number || '-'}</TableCell>
                      <TableCell>{order.order_date || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(order.status)}>
                          {order.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedOrder(order)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Order Details</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <Label className="text-muted-foreground">Customer</Label>
                                  <p className="font-medium">{selectedOrder?.customer_name}</p>
                                </div>
                                <div>
                                  <Label className="text-muted-foreground">Order Type</Label>
                                  <p className="font-medium">{selectedOrder?.order_type}</p>
                                </div>
                                <div>
                                  <Label className="text-muted-foreground">Item Description</Label>
                                  <p className="font-medium">{selectedOrder?.item_description}</p>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <Label className="text-muted-foreground">PO Number</Label>
                                    <p className="font-medium">{selectedOrder?.po_number || '-'}</p>
                                  </div>
                                  <div>
                                    <Label className="text-muted-foreground">Bag No</Label>
                                    <p className="font-medium">{selectedOrder?.bag_no || '-'}</p>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <Label className="text-muted-foreground">Order Date</Label>
                                    <p className="font-medium">{selectedOrder?.order_date || '-'}</p>
                                  </div>
                                  <div>
                                    <Label className="text-muted-foreground">Expected Delivery</Label>
                                    <p className="font-medium">{selectedOrder?.delivery_date || '-'}</p>
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-muted-foreground">Status</Label>
                                  <Badge variant={getStatusBadgeVariant(selectedOrder?.status)}>
                                    {selectedOrder?.status}
                                  </Badge>
                                </div>
                                {selectedOrder?.image_url && (
                                  <div>
                                    <Label className="text-muted-foreground">Order Image</Label>
                                    <img 
                                      src={selectedOrder.image_url} 
                                      alt="Order" 
                                      className="mt-2 max-w-md w-full h-auto rounded-md border"
                                    />
                                  </div>
                                )}
                                {selectedOrder?.notes && (
                                  <div>
                                    <Label className="text-muted-foreground">Notes</Label>
                                    <p className="font-medium">{selectedOrder.notes}</p>
                                  </div>
                                )}
                              </div>
                            </DialogContent>
                          </Dialog>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingOrder(order);
                              setIsEditDialogOpen(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteOrder(order.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Order</DialogTitle>
            <DialogDescription>Update the order details below</DialogDescription>
          </DialogHeader>
          {editingOrder && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Customer Name</Label>
                <Select
                  value={editingOrder.customer_name}
                  onValueChange={(value) => setEditingOrder({ ...editingOrder, customer_name: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(customer => (
                      <SelectItem key={customer.id} value={customer.name}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Order Type</Label>
                <Select
                  value={editingOrder.order_type}
                  onValueChange={(value) => setEditingOrder({ ...editingOrder, order_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="New Order">New Order</SelectItem>
                    <SelectItem value="Repair">Repair</SelectItem>
                    <SelectItem value="Custom">Custom</SelectItem>
                    <SelectItem value="Exchange">Exchange</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Item Description</Label>
                <Textarea
                  value={editingOrder.item_description}
                  onChange={(e) => setEditingOrder({ ...editingOrder, item_description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>PO Number</Label>
                  <Input
                    value={editingOrder.po_number || ''}
                    onChange={(e) => setEditingOrder({ ...editingOrder, po_number: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Bag No</Label>
                  <Input
                    value={editingOrder.bag_no || ''}
                    onChange={(e) => setEditingOrder({ ...editingOrder, bag_no: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Order Date</Label>
                  <Input
                    type="date"
                    value={editingOrder.order_date || ''}
                    onChange={(e) => setEditingOrder({ ...editingOrder, order_date: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Expected Delivery</Label>
                  <Input
                    type="date"
                    value={editingOrder.delivery_date || ''}
                    onChange={(e) => setEditingOrder({ ...editingOrder, delivery_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Status</Label>
                <Select
                  value={editingOrder.status}
                  onValueChange={(value) => setEditingOrder({ ...editingOrder, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Delivered">Delivered</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Order Image (Optional)</Label>
                <div className="space-y-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageSelect(e, true)}
                    className="cursor-pointer"
                  />
                  {editingOrder.image_url && (
                    <div className="relative inline-block">
                      <img 
                        src={editingOrder.image_url} 
                        alt="Order preview" 
                        className="h-32 w-32 object-cover rounded-md border"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="destructive"
                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                        onClick={() => handleRemoveImage(true)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Upload an image of the order (Max 5MB)
                  </p>
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Notes</Label>
                <Textarea
                  value={editingOrder.notes || ''}
                  onChange={(e) => setEditingOrder({ ...editingOrder, notes: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditOrder}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderStatus;